import { useCallback } from 'react';
import {
  useProjectMountActions,
  useProjectMountStatus,
} from '@frameio/connected-surfaces';
import { plural } from '@frameio/i18n';
import { useToggleProjectNotifications } from '@frameio/notifications/hooks';
import {
  useProjectPermissions,
  useWorkspacePermissions,
} from '@frameio/permissions';
import { consumeEvent, log } from '@frameio/utils';
import {
  MenuButton,
  MenuGroup,
  MenuLink,
  MenuSwitch,
  Tooltip,
} from '@frameio/vapor';
import {
  IconArchive,
  IconCog,
  IconCustomFieldLink,
  IconDuplicate,
  IconEject,
  IconHardDrive,
  IconNotifications,
  IconTrash,
  IconUnarchived,
} from '@frameio/vapor-icons';
import {
  useShowDeleteProjectConfirmationModal,
  useShowDuplicateProjectModal,
  useShowProjectSettingsModal,
} from '../../../settings';
import { useProjectStatusToggle } from '../../../shared/hooks';
import { useProjectDetailsFragment } from '../../gql/generated/ProjectDetails';
import TEXT from './generated/TEXT/ContextMenu';

type Props = {
  projectId: string;
};

export const ContextMenu = ({ projectId }: Props) => {
  const showProjectSettings = useShowProjectSettingsModal();
  const { data: project } = useProjectDetailsFragment(projectId);
  const isWorkfront = project?.isWorkfront;
  const isInactive = Boolean(project?.archivedAt);
  const hasDeletePermission = useProjectPermissions({
    check: 'canDelete',
    projectId,
  });
  const canCreate = useWorkspacePermissions({
    check: 'canCreateProject',
    workspaceId: project?.workspace?.id ?? '',
  });
  const canDelete = hasDeletePermission && !isWorkfront;
  const canDuplicate = canCreate && !isInactive && !isWorkfront;
  const canUpdate = useProjectPermissions({
    check: 'canUpdate',
    projectId,
  });

  const handleShowProjectSettings = useCallback(() => {
    showProjectSettings({ projectId });
  }, [projectId, showProjectSettings]);

  const { toggleProjectNotificationsMutation: toggleProjectNotifications } =
    useToggleProjectNotifications({
      projectId: project?.id,
    });

  const showDeleteConfirmation = useShowDeleteProjectConfirmationModal();
  const handleDeleteProject = useCallback(() => {
    if (
      !project ||
      typeof project.id !== 'string' ||
      typeof project.name !== 'string'
    ) {
      return;
    }
    showDeleteConfirmation([{ id: project.id, name: project.name }]);
  }, [project, showDeleteConfirmation]);

  const showDuplicateModal = useShowDuplicateProjectModal();
  const handleDuplicateProject = useCallback(() => {
    if (!project || typeof project.id !== 'string') {
      return;
    }
    showDuplicateModal({ projectId: project.id });
  }, [showDuplicateModal, project]);

  const mountStatus = useProjectMountStatus(projectId);
  const { mountProject, ejectProject } = useProjectMountActions();
  const isMounted = mountStatus === 'mounted';
  const canMount = mountStatus === 'unmounted';

  const handleMountProject = useCallback(() => {
    mountProject(projectId).catch((error: unknown) =>
      log.warn('[ProjectContextMenu] Failed to mount project', error)
    );
  }, [mountProject, projectId]);

  const handleEjectProject = useCallback(() => {
    ejectProject(projectId).catch((error: unknown) =>
      log.warn('[ProjectContextMenu] Failed to eject project', error)
    );
  }, [ejectProject, projectId]);

  const { handleStatusToggleClick } = useProjectStatusToggle({
    archivedAt: project.archivedAt,
    id: project.id ?? '',
    name: project.name ?? '',
    refetch: true,
    trackingSource: 'ProjectSettingsFromWorkspace',
    workspaceId: project.workspace?.id ?? '',
  });

  if (!project) {
    return null;
  }

  const onWorkfrontLink = () => {
    window.open(project.workfrontProjectLink ?? '', '_blank');
  };

  return (
    <div onClick={consumeEvent}>
      <MenuGroup label={TEXT.menuTitle}>
        {isMounted && (
          <MenuButton
            data-dd-action-name="workspaces.project-eject"
            iconBefore={<IconEject />}
            onPress={handleEjectProject}
            variant="destructive"
          >
            {TEXT.eject}
          </MenuButton>
        )}

        {canMount && (
          <MenuButton
            data-dd-action-name="workspaces.project-mount"
            iconBefore={<IconHardDrive />}
            onPress={handleMountProject}
          >
            {TEXT.mount}
          </MenuButton>
        )}

        <MenuSwitch
          checked={project.notificationsEnabled ?? false}
          data-dd-action-name="workspaces.project-toggle-notifications"
          iconBefore={<IconNotifications />}
          name={TEXT.notifications}
          onPress={toggleProjectNotifications}
          shouldCloseMenuOnSelect={true}
        >
          {TEXT.notifications}
        </MenuSwitch>

        {project.isWorkfront && (
          <Tooltip
            disabled={Boolean(project.workfrontProjectLink)}
            title={TEXT.linkDisabled}
          >
            <span>
              <MenuLink
                data-dd-action-name="workspaces.project-open-workfront"
                disabled={!project.workfrontProjectLink}
                iconBefore={<IconCustomFieldLink />}
                onClick={onWorkfrontLink}
              >
                {TEXT.workfrontLink}
              </MenuLink>
            </span>
          </Tooltip>
        )}

        {!isInactive && (
          <MenuButton
            data-dd-action-name="workspaces.project-duplicate"
            disabled={!canDuplicate}
            iconBefore={<IconDuplicate />}
            onPress={handleDuplicateProject}
          >
            {TEXT.duplicateProject}
          </MenuButton>
        )}

        <MenuButton
          data-dd-action-name="workspaces.project-toggle-status"
          disabled={!canUpdate}
          iconBefore={isInactive ? <IconUnarchived /> : <IconArchive />}
          onPress={handleStatusToggleClick}
        >
          {isInactive ? TEXT.markActive : TEXT.markInactive}
        </MenuButton>

        <MenuButton
          data-dd-action-name="workspaces.project-settings"
          disabled={false}
          iconBefore={<IconCog />}
          onPress={handleShowProjectSettings}
        >
          {TEXT.projectSettings}
        </MenuButton>
      </MenuGroup>

      {!isWorkfront && (
        <MenuGroup>
          <MenuButton
            data-dd-action-name="workspaces.project-delete"
            disabled={!canDelete}
            iconBefore={<IconTrash />}
            onPress={handleDeleteProject}
            variant="destructive"
          >
            {TEXT.delete}
          </MenuButton>
        </MenuGroup>
      )}
    </div>
  );
};

TEXT.DEFINE(
  {
    /** Menu item label to delete a project */
    delete: 'Delete',
    /** Menu item label to duplicate a project */
    duplicateProject: 'Duplicate Project',
    /** Menu item label to eject a project mounted by the Frame.io Drive app */
    eject: 'Eject',
    /** Tooltip text when Workfront link is unavailable */
    linkDisabled: 'Link currently unavailable',
    /** Menu item label to mark project active */
    markActive: 'Mark Active',
    /** Menu item label to mark project inactive */
    markInactive: 'Mark Inactive',
    /** Menu group label for project actions */
    menuTitle: 'Project',
    /** Menu item label to mount a project with the Frame.io Drive app */
    mount: 'Mount Project',
    /** Menu item label for project notifications */
    notifications: 'Notifications',
    /** Menu item label to open project settings */
    projectSettings: 'Project Settings',
    /** Menu item label to open in Workfront */
    workfrontLink: 'Open in Workfront',
  },
  {
    plural,
  }
);
